import { 
  getHypotenuse, 
  convertRadiansToDegress,
  roundTo3sf, 
} from './math'
import { memorize } from './../utils/optimization'


export interface Coordinate {
  x: number;
  y: number;
}

/**
 * Subtract coordinate B from coordinate A.
 */
export const subtractCoords = function(coordA: Coordinate, coordB: Coordinate){
  return {
    x: coordA.x - coordB.x,
    y: coordA.y - coordB.y,
  }
}

export const addCoords = function(coordA: Coordinate, coordB: Coordinate){
  return {
    x: coordA.x + coordB.x,
    y: coordA.y + coordB.y,
  }
}

export const scaleCoord = function(coord: Coordinate, scale: number){
  return {
    x: coord.x * scale,
    y: coord.y * scale,
  }
}

export const getDistanceBetweenCoords = function(coordA: Coordinate, coordB: Coordinate){
  var temp = subtractCoords(coordB, coordA)
  return getHypotenuse(temp.x, temp.y)
}

/**
 * Get the mid point between two coordinates.
 */
export const getMidPointCoords = function(coordA: Coordinate, coordB: Coordinate){
  var temp = subtractCoords(coordA, coordB)
  return {
    x: (coordA.x + coordB.x) / 2,
    y: (coordA.y + coordB.y) / 2,
  }
}


/**
 * Rotate a coordinate around an origin. 
 * @param coord The coordinate to rotate
 * @param angle The rotation angle in radians
 * @param origin If provided rotates around this origin, otherwise
 * assumes the origin is 0,0
 */
export const rotateAroundOrigin = function(
  coord: Coordinate,
  angle: number,
  origin?: Coordinate
): Coordinate {

  if (!origin){
    origin = {
      x: 0,
      y: 0,
    }
  }

  var coordRelativeToOrigin = {
    x: coord.x - origin.x,
    y: coord.y - origin.y,
  }

  var { x, y } = coordRelativeToOrigin;
  var cos = Math.cos(angle);
  var sin = Math.sin(angle);

  var rotatedCoordRelativeToOrigin = {
    x: cos * x + sin * y,
    y: cos * y - sin * x,
  }

  return {
    x: rotatedCoordRelativeToOrigin.x + origin.x,
    y: rotatedCoordRelativeToOrigin.y + origin.y,
  }
};


/**
 * Given a coordinate, if a line was drawn from the 0,0 origin to this coordinate
 * then return the angle of this line as if it was a rotation around the z axis
 * |
 * |         x (2,2)
 * |
 * |
 * |________________________
 * 0,0
 * Returns the angle in radians
 */
export const getClockAngle = function(coord: Coordinate): number {
  var { x, y } = coord;

  // Depending on if the coordinate is in the north-east, south-east, south-west
  // or north-west side of the x and y axises we solve the problem for each
  // quadrant and then add the respective angle multiples of 90°

  // Math.atan will give 180 degree different answers if you use a negative
  // 0 instead of a positive 0 🤷, effectively you're dividing by 0 so doing
  // atan on Infinity and -Infinity
  if (y == 0) {
    y = 0;
  }

  if (x == 0 && y == 0) return 0;

  // This is the north-east section of the axises
  if (x >= 0 && y >= 0) {
    // This gives us the angle between the y-axis and the hypothenus
    return Math.atan(x / y);
  } else if (x >= 0 && y < 0) {
    // The south-east section
    var theta = Math.atan((y * -1) / x);
    // Add 90° to the angle
    return Math.PI / 2 + theta;
  } else if (x < 0 && y < 0) {
    // The south-west section
    var theta = Math.atan((x * -1) / (y * -1));
    // Add 180° to the angle
    return Math.PI + theta;
  } else {
    var theta = Math.atan(y / (x * -1));
    // Add 270° to the angle
    return (Math.PI * 3) / 2 + theta;
  }
};

/**
 * Similar to `getClockAngle`, however does not assume a 0,0 origin
 * and instead takes the first line point as the origin. 
 */
export const getLineClockAngle = function(line: Array<Coordinate>): number{
  var temp = subtractCoords(line[0], line[1])

  // This is the clock angle of the line, i.e. if the line were vertical
  // how much would you need to rotate it clockwise in order to match
  // it's orientation.
  return getClockAngle(temp)

}


/**
 * Convert a coordinates point to 3 significant figures.
 */
export const convertPointTo3sf = function(point: Coordinate) : Coordinate {
  return {
    x: roundTo3sf(point.x),
    y: roundTo3sf(point.y),
  }
}
/*  {
    'cacheKey': point => 'convertPointTo3sf:' + JSON.stringify(point),
  }
)*/


export const trimLine = memorize(
  function(line: Array<Coordinate>, trim1: number, trim2: number){

    // Rotate the line so that it's vertical
    var clockAngle = getLineClockAngle(line)

    var newLineStart = {
      x: line[0].x,
      y: line[0].y - trim1,
    }

    var lineEndRotated = rotateAroundOrigin(
      line[1],
      - clockAngle,
      line[0],
    )

    // Apply the trim
    var newLineEnd = {
      x: lineEndRotated.x,
      y: lineEndRotated.y + trim2,
    }

    return [
      rotateAroundOrigin(
        newLineStart,
        clockAngle,
        line[0],
      ),
      rotateAroundOrigin(
        newLineEnd,
        clockAngle,
        line[0],
      )
    ]
  },
  {
    cacheKey: (line, trim1, trim2) => 'trimLine:' + JSON.stringify(line) + ':' + JSON.stringify(trim1) + ':' + JSON.stringify(trim2),
  }
)
